import React, { createContext, useContext, useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, userData: any) => Promise<{ error: any, user: any }>;
  signOut: () => Promise<{ error: any }>;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing Supabase auth...');
        
        // Set a maximum timeout for the entire initialization
        timeoutId = setTimeout(() => {
          console.warn('⚠️ Auth initialization timed out after 10 seconds');
          setInitError('Authentication initialization timed out. Please refresh the page.');
          setLoading(false);
        }, 10000);
        
        // Get initial session with timeout
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('SESSION_TIMEOUT')), 8000);
        });
        
        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]) as any;
        
        clearTimeout(timeoutId);
        
        if (error) {
          console.error('❌ Error getting Supabase session:', error);
          setInitError(`Session error: ${error.message}`);
        } else {
          console.log('✅ Session retrieved successfully:', session ? 'User logged in' : 'No active session');
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.message === 'SESSION_TIMEOUT') {
          console.warn('⚠️ Session check timed out after 8 seconds');
          setInitError('Session check timed out. Please check your internet connection and refresh.');
        } else {
          console.error('❌ Unexpected error during auth initialization:', error);
          setInitError(`Initialization error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      } finally {
        setLoading(false);
      }
    };
    
    // Get initial session
    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('🔄 Auth state changed:', _event, session ? 'User logged in' : 'No session');
      
      // Set flag when user signs in (for organization selector)
      if (_event === 'SIGNED_IN' && session) {
        sessionStorage.setItem('justLoggedIn', 'true');
        console.log('✅ User signed in - setting justLoggedIn flag');
      }
      
      // Clear flag when user signs out
      if (_event === 'SIGNED_OUT') {
        sessionStorage.removeItem('justLoggedIn');
        console.log('✅ User signed out - clearing justLoggedIn flag');
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 AuthContext: Attempting sign in for:', email);
      console.log('🔐 Supabase URL:', import.meta.env.VITE_SUPABASE_URL);
      console.log('🔐 Supabase Key (first 20 chars):', import.meta.env.VITE_SUPABASE_ANON_KEY?.substring(0, 20));

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('❌ Sign in error:', error);
        console.error('❌ Error name:', error.name);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error status:', error.status);
        return { error };
      }

      console.log('✅ Sign in successful:', data);
      return { error: null };
    } catch (err) {
      console.error('💥 Unexpected sign in error:', err);
      return { error: { message: 'An unexpected error occurred during sign in' } };
    }
  };

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      // Use production URL if available, fallback to current origin
      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
      const redirectUrl = `${appUrl}/auth`;
      
      console.log('🔗 Email confirmation will redirect to:', redirectUrl);
      
      // Store user's organization choice in metadata
      const userMetadata: any = {
        name: userData.name,
        role: userData.role || 'technician'
      };
      
      // If user explicitly wants to create their own org, store this flag
      if (userData.create_own_org === true) {
        userMetadata.create_own_org = true;
        console.log('📝 User chose to create their own organization');
      }
      
      // If user wants to join invited org, store this flag
      if (userData.join_invited_org === true) {
        userMetadata.join_invited_org = true;
        console.log('📝 User chose to join invited organization');
      }
      
      // If user chose both, log it
      if (userData.create_own_org === true && userData.join_invited_org === true) {
        console.log('📝 User chose BOTH: join invited org AND create own organization');
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userMetadata,
          emailRedirectTo: redirectUrl
        },
      });
      
      if (error) {
        console.error('Sign up error:', error);
        return { error, user: null };
      }
      
      return { error: null, user: data.user };
    } catch (err) {
      console.error('Unexpected sign up error:', err);
      return { 
        error: { message: 'An unexpected error occurred during sign up' }, 
        user: null 
      };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign out error:', error);
        return { error };
      }
      
      // Clear any local storage items that might still exist
      localStorage.removeItem('currentUser');
      localStorage.removeItem('teamMembers');
      localStorage.removeItem('currentOrganizationId'); // Clear org selection so selector shows on next login
      localStorage.removeItem('impersonatedUserId');
      localStorage.removeItem('actualUserId');
      
      return { error: null };
    } catch (err) {
      console.error('Unexpected sign out error:', err);
      return { error: { message: 'An unexpected error occurred during sign out' } };
    }
  };

  const value = {
    session,
    user,
    signIn,
    signUp,
    signOut,
    loading,
    isAuthenticated: !!user,
    initError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};